import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import SingleChoiceStyles from "./SegmentedControlSingleChoice.module.css";
import MultiChoiceStyles from "./SegmentedControlMultiChoice.module.css";
import { useFocusHandling } from "../../../src/hooks/useFocusHandling";

export type SegmentedControlAtomType = {
  className?: string;
  label?: string;

  /** Variant props */
  size?: "sm" | "md" | "lg";
  state?: "default" | "selected" | "disabled";

  /** Custom props */
  icon?: ReactNode;
  type?: "single" | "multi";
  isSelected?: boolean;
  value?: string;
  onClick?: (e: string) => void;
};

const SegmentedControlSyles = {
  single: SingleChoiceStyles,
  multi: MultiChoiceStyles,
};

const SegmentedControlAtom: FunctionComponent<SegmentedControlAtomType> = ({
  className = "",
  size = "sm",
  state: externalState = "default",
  label = "Label",
  icon,
  type = "single",
  value = "",
  isSelected = false,
  onClick,
}) => {
  const styles = SegmentedControlSyles[type];
  const [state, setState] = useState(externalState);
  const [originalState] = useState(externalState);
  const segmentedControlId = `segmented-control-${size}-${state}-${type}`;
  const [focus, setFocus] = useState(false);
  const { handleFocus, handleBlur } = useFocusHandling({
    setHighlight: setFocus,
    isDisabled: state === "disabled",
  });

  const handleClick = () => {
    if (state === "disabled") return;

    if (onClick) {
      onClick(value);
    }

    setState("selected");
  };

  useEffect(() => {
    setState(externalState);
  }, [externalState]);

  useEffect(() => {
    setState(
      isSelected
        ? "selected"
        : originalState === "disabled"
        ? "disabled"
        : "default"
    );
  }, [isSelected]);

  return (
    <div
      className={[
        styles.root,
        className,
        "quill-ui-next-atom",
        "quill-ui-next-no-select",
        label === "" && "quill-ui-next-fit-content",
        state !== "disabled" && "quill-ui-next-pointer",
      ].join(" ")}
      data-id={segmentedControlId}
      data-size={size}
      data-state={state}
      tabIndex={state === "disabled" ? -1 : 0}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
    >
      {icon && <div className={styles.customIcon}>{icon}</div>}
      {label && <div className={styles.label}>{label}</div>}
      {focus && <div className={styles.bgFocus} />}
      {/* This element is important to have a reference icon color */}
      <span className={`quill-ui-next-hide ${styles.label}`}></span>
    </div>
  );
};

export default SegmentedControlAtom;
